import L from 'leaflet';
import { eventBus } from "@/main";
import { useFleetStore } from '@/stores/fleet';

export class ShipsLayer {
    constructor(map, layerControl) {
        this.map = map;
        this.layerControl = layerControl;
        this.shipMarkersGroup = null;
        this.selectedShipId = null;
        this.routeLayer = null; // Слой для маршрута
        this.portsData = null; // Кэш данных о портах
    }

    addShipLayer() {
        if (!this.map) return;

        this.shipMarkersGroup = L.layerGroup().addTo(this.map);
    }

    // Отрисовка кораблей на карте
    drawShips(ships, selectedShipId = null) {
        if (!this.map) {
            console.error("Map not initialized for drawing ships");
            return;
        }

        // Очищаем предыдущие маркеры, если они есть
        if (this.shipMarkersGroup) {
            this.shipMarkersGroup.clearLayers();

            // Добавляем группу маркеров на карту, если она еще не добавлена
            if (!this.map.hasLayer(this.shipMarkersGroup)) {
                this.shipMarkersGroup.addTo(this.map);
            }
        } else {
            // Создаем новую группу маркеров, если она еще не существует
            this.shipMarkersGroup = L.layerGroup().addTo(this.map);
        }

        if (!ships || ships.length === 0) {
            return;
        }

        // Создаем маркеры для каждого корабля
        ships.forEach(ship => {
            if (!ship.position || !ship.position.lat || !ship.position.lon) {
                return;
            }

            const marker = L.marker([ship.position.lat, ship.position.lon], {
                icon: this.createShipIcon(ship),
                title: ship.name
            });

            // Создаем попап с базовым содержимым
            const popup = L.popup({
                closeButton: true,
                className: 'ship-popup',
                maxWidth: 450,
                maxHeight: 400
            }).setContent('<div class="loading">Загрузка данных...</div>');

            marker.bindPopup(popup);

            // Обработчик открытия попапа для загрузки дополнительных данных
            marker.on('popupopen', async () => {
                try {
                    const popupContent = await this.createShipPopupContent(ship);
                    popup.setContent(popupContent);
                    
                    // Построить маршрут если есть данные о портах
                    const cacheData = await this.loadShipCacheData(ship.imo);
                    if (cacheData && cacheData.ports) {
                        // Проверяем формат данных портов
                        let portsArray;
                        if (Array.isArray(cacheData.ports)) {
                            // Массив портов (как обычно)
                            portsArray = cacheData.ports;
                        } else if (typeof cacheData.ports === 'object' && cacheData.ports.portSign) {
                            // Одиночный порт в виде объекта - преобразуем в массив
                            portsArray = [cacheData.ports];
                        }
                        
                        if (portsArray && portsArray.length > 0) {
                            await this.drawShipRoute(portsArray);
                        }
                    }
                } catch (error) {
                    console.error('Ошибка загрузки данных корабля:', error);
                    // Показываем базовые данные при ошибке
                    const basicContent = this.createBasicPopupContent(ship);
                    popup.setContent(basicContent);
                }
            });
            
            // Обработчик закрытия попапа для очистки маршрута
            marker.on('popupclose', () => {
                this.clearRoute();
            });

            // Сохраняем deviceId в маркере для дальнейшего использования
            marker.shipDeviceId = ship.value;

            // Обработка клика по маркеру
            marker.on('click', (e) => {
                L.DomEvent.stopPropagation(e);
                // Выбрать корабль в таблице
                eventBus.$emit('select-ship', ship.value);
            });

            this.shipMarkersGroup.addLayer(marker);

            // Если есть выбранный deviceId, открываем попап
            if (selectedShipId && ship.value === selectedShipId) {
                marker.openPopup();
            }
        });
    }

    // Создание иконки корабля
    createShipIcon(ship) {
        const speed = parseFloat(ship.speed) || 0;
        const course = parseFloat(ship.aisCOG || 0);

        // Доступ к хранилищу для проверки предупреждений
        const fleetStore = useFleetStore();

        // Проверяем наличие предупреждений для Navarea
        const hasNavareaWarning = fleetStore.alerts &&
            fleetStore.alerts.navarea &&
            fleetStore.alerts.navarea[ship.value];

        // Проверяем наличие предупреждений о погоде
        const hasWeatherWarning = fleetStore.weather &&
            fleetStore.weather[ship.value] &&
            fleetStore.weather[ship.value].hasWarning;

        // Цвет иконки корабля в зависимости от скорости
        let iconColor = speed > 0 ? 'green' : 'grey';

        // Цвет если данные устарели
        if (ship.isOldData) {
            iconColor = 'yellow';
        }

        // Цвет если есть предупреждения Navarea или погоды
        if (hasNavareaWarning || hasWeatherWarning) {
            iconColor = 'red';
        }

        const iconUrl = `/assets/ship_1tx_${iconColor}.png`;

        return L.divIcon({
            className: 'ship-icon-container',
            html: `<img src="${iconUrl}" style="transform: rotate(${course}deg); width: 20px; height: 40px;">`,
            iconSize: [20, 40],
            iconAnchor: [10, 20]
        });

    }

    // Форматирование значения поля (включая вложенные объекты)
    formatFieldValue(value, level = 0) {
        if (value === null || value === undefined || value === '' || value === '-') {
            return null; // Возвращаем null для пустых значений, чтобы их можно было скрыть
        }
        
        if (Array.isArray(value)) {
            if (value.length === 0) return null;
            
            // Если массив содержит объекты, форматируем каждый объект
            if (value.some(item => typeof item === 'object' && item !== null)) {
                let result = '';
                let hasContent = false;
                
                value.forEach((item, index) => {
                    if (typeof item === 'object' && item !== null) {
                        let itemContent = '';
                        let itemHasContent = false;
                        
                        for (const [subKey, subValue] of Object.entries(item)) {
                            const formattedSubValue = this.formatFieldValue(subValue, level + 2);
                            if (formattedSubValue !== null) {
                                const displaySubKey = this.getDisplayName(subKey);
                                itemContent += `<p style="margin: 4px 0;"><span style="color: #333;">${displaySubKey}:</span> ${formattedSubValue}</p>`;
                                itemHasContent = true;
                            }
                        }
                        
                        if (itemHasContent) {
                            // Показываем номер только если элементов больше одного
                            if (value.length > 1) {
                                result += `<p style="margin: 4px 0;"><span style="color: #333;">[${index + 1}]:</span></p>`;
                            }
                            result += itemContent;
                            hasContent = true;
                        }
                    } else if (item !== null && item !== undefined && item !== '' && item !== '-') {
                        result += `<p style="margin: 4px 0;"><span style="color: #666;">${item}</span></p>`;
                        hasContent = true;
                    }
                });
                
                return hasContent ? result : null;
            } else {
                // Обычный массив строк/чисел, фильтруем пустые значения
                const filteredArray = value.filter(item => item !== null && item !== undefined && item !== '' && item !== '-');
                return filteredArray.length > 0 ? `<span style="color: #666;">${filteredArray.join(', ')}</span>` : null;
            }
        }
        
        if (typeof value === 'object' && !Array.isArray(value)) {
            let result = '';
            let hasContent = false;
            
            for (const [subKey, subValue] of Object.entries(value)) {
                const formattedSubValue = this.formatFieldValue(subValue, level + 1);
                if (formattedSubValue !== null) {
                    const displaySubKey = this.getDisplayName(subKey);
                    result += `<p style="margin: 4px 0;"><span style="color: #333;">${displaySubKey}:</span> ${formattedSubValue}</p>`;
                    hasContent = true;
                }
            }
            
            return hasContent ? result : null;
        }
        
        return `<span style="color: #666;">${value.toString()}</span>`;
    }

    // Получение отображаемого имени для поля
    getDisplayName(key) {
        const fieldNameMap = {
            'mmsi': 'MMSI',
            'vessel_type': 'Vessel type',
            'type_specific': 'Vessel specific',
            'build_year': 'Build year',
            'length': 'Length',
            'beam': 'Beam',
            'gross_tonnage': 'Tonnage',
            'deadweight': 'Deadweight',
            'navigational_status': 'Navigational status',
            'last_port': 'Last port',
            'eta_utc': 'ETA (UTC)',
            'atd_utc': 'ATD (UTC)',
            'current_draught': 'Current draught',
            'position_received': 'Position received',
            'unlocode_destination': 'Destination',
            'teu': 'TEU',
            'pi': 'PI',
            'fuel_type': 'Fuel',
            'engine_type': 'Type',
            'engine_builder': 'Builder',
            'engine_power_kw': 'Power (kw)',
            'ism': 'ISM',
            'ism_email': 'ISM email',
            'ism_website': 'ISM website',
            'ism_address': 'ISM address',
            'manager_email': 'Manager email',
            'manager_website': 'Manager website',
            'manager_address': 'Manager address',
            'registered_owner': 'Owner',
            'registered_owner_email': 'Owner email',
            'registered_owner_website': 'Owner website',
            'registered_owner_address': 'Owner address',
            'classification_society': 'Classification society'
        };
        
        return fieldNameMap[key.toLowerCase()] || (key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'));
    }

    // Загрузка дополнительных данных корабля из кэша
    async loadShipCacheData(shipImo) {
        if (!shipImo) return null;
        
        try {
            const response = await fetch(`/ais/cache/${shipImo}/ship_data.json`);
            if (response.ok) {
                return await response.json();
            }
        } catch (error) {
            console.log(`Дополнительные данные для ${shipImo} не найдены:`, error);
        }
        return null;
    }

    // Проверка наличия изображения корабля в кэше
    async checkShipImage(shipImo) {
        if (!shipImo) return null;
        
        try {
            const response = await fetch(`/ais/cache/${shipImo}/ship_image.jpg`, { method: 'HEAD' });
            if (response.ok) {
                return `/ais/cache/${shipImo}/ship_image.jpg`;
            }
        } catch (error) {
            console.log(`Изображение для ${shipImo} не найдено:`, error);
        }
        return null;
    }

    // Загрузка данных о портах
    async loadPortsData() {
        if (this.portsData) return this.portsData;
        
        try {
            const response = await fetch('/ports.json');
            if (response.ok) {
                const portsArray = await response.json();
                // Преобразуем массив в объект для удобного поиска по port_code
                this.portsData = {};
                if (Array.isArray(portsArray)) {
                    portsArray.forEach(port => {
                        if (port.port_code && port.lat && port.lon) {
                            // Если порт с таким кодом уже есть, берём первый (обычно основной)
                            if (!this.portsData[port.port_code]) {
                                this.portsData[port.port_code] = {
                                    name: port.name,
                                    country: port.country,
                                    coordinates: [port.lon, port.lat] // [longitude, latitude] как в оригинальном формате
                                };
                            }
                        }
                    });
                }
                return this.portsData;
            }
        } catch (error) {
            console.log('Ошибка загрузки данных о портах:', error);
        }
        return null;
    }

    // Группировка последовательных визитов в один порт
    groupPortVisits(ports) {
        if (!ports || ports.length === 0) return [];
        
        // Если только один порт, возвращаем его как группу
        if (ports.length === 1) {
            const port = ports[0];
            return [{
                portSign: port.portSign,
                portName: port.portName,
                firstArrived: port.arrived,
                lastDeparted: port.departed,
                visits: [port],
                originalIndex: 0
            }];
        }
        
        // Сначала разворачиваем массив для обработки в хронологическом порядке
        const reversedPorts = [...ports].reverse();
        
        const grouped = [];
        let currentGroup = null;
        
        reversedPorts.forEach((port, index) => {
            if (!currentGroup || currentGroup.portSign !== port.portSign) {
                // Начинаем новую группу
                if (currentGroup) {
                    grouped.push(currentGroup);
                }
                currentGroup = {
                    portSign: port.portSign,
                    portName: port.portName,
                    firstArrived: port.arrived, // Дата прибытия из первого визита (в хронологическом порядке)
                    lastDeparted: port.departed, // Дата отправления из первого визита (будет обновлена)
                    visits: [port],
                    originalIndex: ports.length - 1 - index // Преобразуем индекс обратно к исходному массиву
                };
            } else {
                // Продолжаем текущую группу
                currentGroup.visits.push(port);
                // Обновляем дату отъезда на дату отъезда из текущего (последнего в хронологической последовательности) визита
                if (port.departed) {
                    currentGroup.lastDeparted = port.departed;
                }
            }
        });
        
        // Добавляем последнюю группу
        if (currentGroup) {
            grouped.push(currentGroup);
        }
        
        // Возвращаем в исходном порядке (последние порты первые)
        return grouped.reverse();
    }

    // Построение маршрута по портам
    async drawShipRoute(ports) {
        if (!ports || !Array.isArray(ports) || ports.length === 0) return;
        
        const portsData = await this.loadPortsData();
        if (!portsData) return;
        
        // Очищаем предыдущий маршрут
        this.clearRoute();
        
        // Создаем слой для маршрута
        this.routeLayer = L.layerGroup().addTo(this.map);
        
        // Группируем последовательные визиты
        const groupedPorts = this.groupPortVisits(ports);
        const routePoints = [];
        
        // Проходим по сгруппированным портам
        groupedPorts.forEach((portGroup, index) => {
            const portInfo = portsData[portGroup.portSign];
            if (portInfo && portInfo.coordinates) {
                const [lng, lat] = portInfo.coordinates;
                const portLatLng = L.latLng(lat, lng);
                routePoints.push(portLatLng);
                
                // Создаем маркер порта
                const portMarker = L.circleMarker(portLatLng, {
                    radius: 6,
                    fillColor: index === 0 ? '#ff0000' : '#0066cc', // Красный для последнего, синий для остальных
                    color: '#ffffff',
                    weight: 2,
                    opacity: 1,
                    fillOpacity: 0.8
                });
                
                // Определяем размер текста для вычисления размера фона
                const portName = portInfo.name;
                
                // Формируем текст с датами для тултипа
                let dateText;
                if (portGroup.visits.length === 1) {
                    // Один визит
                    const visit = portGroup.visits[0];
                    if (visit.departed) {
                        dateText = `${visit.arrived} - ${visit.departed}`;
                    } else {
                        dateText = `${visit.arrived} (current)`;
                    }
                } else {
                    // Несколько визитов
                    dateText = `${portGroup.firstArrived} - ${portGroup.lastDeparted || 'current'}`;
                }
                
                const textWidth = Math.max(portName.length * 7, dateText.length * 6, 100); // Учитываем обе строки
                
                // Создаем подпись порта с правильным размером фона и датами
                const portLabel = L.divIcon({
                    className: 'port-label-custom',
                    html: `<div style="
                        background: rgba(255,255,255,0.95); 
                        padding: 4px 8px; 
                        border-radius: 4px; 
                        font-size: 11px; 
                        font-weight: bold; 
                        color: #333; 
                        border: 1px solid #ccc;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                        white-space: nowrap;
                        text-align: center;
                        min-width: ${textWidth}px;
                        line-height: 1.2;
                    ">
                        <div>${portName}</div>
                        <div style="font-size: 9px; font-weight: normal; color: #666; margin-top: 2px;">${dateText}</div>
                    </div>`,
                    iconSize: [textWidth + 16, 32],
                    iconAnchor: [(textWidth + 16) / 2, -5]
                });
                
                const labelMarker = L.marker(portLatLng, { icon: portLabel });
                
                // Формируем текст о посещении порта
                let visitText;
                if (portGroup.visits.length === 1) {
                    // Один визит
                    const visit = portGroup.visits[0];
                    visitText = `<strong>Visit:</strong> ${visit.arrived}${visit.departed ? ` - ${visit.departed}` : ' (still in port)'}`;
                } else {
                    // Несколько визитов
                    visitText = `<strong>Multiple visits (${portGroup.visits.length}):</strong><br>
                        ${portGroup.firstArrived} - ${portGroup.lastDeparted || 'still in port'}`;
                }
                
                // Добавляем попап с информацией о порте
                const popupContent = `
                    <div style="min-width: 220px; max-width: 300px;">
                        <h6 style="margin: 0 0 8px 0; color: #2c3e50;">${portInfo.name}</h6>
                        <p style="margin: 4px 0;"><strong>Code:</strong> ${portGroup.portSign}</p>
                        <p style="margin: 4px 0;">${visitText}</p>
                        ${portInfo.country ? `<p style="margin: 4px 0;"><strong>Country:</strong> ${portInfo.country}</p>` : ''}
                    </div>
                `;
                portMarker.bindPopup(popupContent);
                
                this.routeLayer.addLayer(portMarker);
                this.routeLayer.addLayer(labelMarker);
            }
        });
        
        // Рисуем линию маршрута
        if (routePoints.length > 1) {
            const routeLine = L.polyline(routePoints, {
                color: '#0066cc',
                weight: 3,
                opacity: 0.7,
                dashArray: '5, 10'
            });
            this.routeLayer.addLayer(routeLine);
        }
    }

    // Очистка маршрута
    clearRoute() {
        if (this.routeLayer) {
            if (this.map && this.map.hasLayer && this.map.hasLayer(this.routeLayer)) {
                this.map.removeLayer(this.routeLayer);
            }
            this.routeLayer.clearLayers && this.routeLayer.clearLayers();
            this.routeLayer = null;
        }
    }

    // Создание контента для попапа корабля
    async createShipPopupContent(ship) {
        // AIS cтатус
        let aisStatusHtml = '';
        if (ship.aisStatus) {
            const statusClass = ship.aisStatusVariant || 'secondary';
            aisStatusHtml = `
        <div class="mt-2 mb-2">
          <span class="badge badge-${statusClass}">
            ${ship.aisStatus}
          </span>
        </div>
      `;
        }

        // Загружаем дополнительные данные из кэша
        const cacheData = await this.loadShipCacheData(ship.imo);
        const imageUrl = await this.checkShipImage(ship.imo);

        // Формируем HTML для дополнительных данных
        let additionalDataHtml = '';
        if (cacheData) {
            additionalDataHtml = '<hr><div class="additional-data">';
            
            // Поля которые должны отображаться с горизонтальной линией
            const fieldsWithSeparator = ['engine', 'management'];
            
            // Поля для секции Construction
            const constructionFields = ['build_year', 'length', 'beam', 'gross_tonnage', 'deadweight'];
            
            // Отображаем все поля из кэша (кроме стандартных и исключенных)
            const excludeFields = ['name', 'imo', 'position', 'speed', 'destination', 'eta', 'image_url', 'has_image', 'api_update_time', 'data_source', 'ports', 'country_iso', 'unlocode_lastport', 'api_location_update_time', 'location_last_update', 'cache_update_time', 'build_year', 'length', 'beam', 'gross_tonnage', 'deadweight'];
            
            // Сначала отображаем обычные поля
            for (const [key, value] of Object.entries(cacheData)) {
                if (!excludeFields.includes(key.toLowerCase()) && !fieldsWithSeparator.includes(key.toLowerCase()) && value !== null && value !== undefined && value !== '') {
                    const displayKey = this.getDisplayName(key);
                    const formattedValue = this.formatFieldValue(value);
                    if (formattedValue !== null) {
                        additionalDataHtml += `<p style="margin: 4px 0;"><span style="color: #333;">${displayKey}:</span> ${formattedValue}</p>`;
                    }
                }
            }
            
            // Отображаем секцию Construction
            let hasConstructionData = false;
            let constructionHtml = '';
            constructionFields.forEach(fieldKey => {
                if (cacheData[fieldKey]) {
                    const value = cacheData[fieldKey];
                    const formattedValue = this.formatFieldValue(value);
                    if (formattedValue !== null) {
                        const displayKey = this.getDisplayName(fieldKey);
                        constructionHtml += `<p style="margin: 4px 0;"><span style="color: #333;">${displayKey}:</span> ${formattedValue}</p>`;
                        hasConstructionData = true;
                    }
                }
            });
            
            if (hasConstructionData) {
                additionalDataHtml += '<hr style="margin: 8px 0; border-color: #ddd;">';
                additionalDataHtml += '<h6 style="margin: 4px 0 8px 0; color: #2c3e50; font-size: 12px;">Construction</h6>';
                additionalDataHtml += constructionHtml;
            }
            
            // Затем отображаем поля с разделителями в нужном порядке (Engine перед Management)
            ['engine', 'management'].forEach(fieldKey => {
                if (cacheData[fieldKey]) {
                    const value = cacheData[fieldKey];
                    const formattedValue = this.formatFieldValue(value);
                    if (formattedValue !== null) {
                        additionalDataHtml += '<hr style="margin: 8px 0; border-color: #ddd;">';
                        const displayKey = this.getDisplayName(fieldKey);
                        additionalDataHtml += `<h6 style="margin: 4px 0 8px 0; color: #2c3e50; font-size: 12px;">${displayKey}</h6>`;
                        additionalDataHtml += formattedValue;
                    }
                }
            });
            additionalDataHtml += '</div>';
        }

        // Формируем HTML для изображения
        let imageHtml = '';
        if (imageUrl) {
            imageHtml = `
        <div class="ship-image mt-2">
          <img src="${imageUrl}" alt="Ship image" style="max-width: 100%; max-height: 150px; border-radius: 4px;" />
        </div>
      `;
        }

        return `
      <div class="ship-popup-content scrollable-content">
        <h6>${ship.name || 'Unknown vessel'}</h6>
        ${aisStatusHtml}
        ${imageHtml}
        <p>IMO: ${ship.imo || 'N/A'}</p>
        <p>Position: ${ship.position.lat.toFixed(5)}, ${ship.position.lon.toFixed(5)}</p>
        <p>Speed: ${ship.speed || '0'} knots</p>
        ${ship.destination ? `<p>Destination: ${ship.destination}</p>` : ''}
        ${ship.eta ? `<p>ETA: ${new Date(ship.eta).toLocaleString()}</p>` : ''}
        ${additionalDataHtml}
      </div>
    `;
    }

    // Создание базового контента для попапа (без дополнительных данных)
    createBasicPopupContent(ship) {
        // AIS cтатус
        let aisStatusHtml = '';
        if (ship.aisStatus) {
            const statusClass = ship.aisStatusVariant || 'secondary';
            aisStatusHtml = `
        <div class="mt-2 mb-2">
          <span class="badge badge-${statusClass}">
            ${ship.aisStatus}
          </span>
        </div>
      `;
        }

        return `
      <div class="ship-popup-content">
        <h6>${ship.name || 'Unknown vessel'}</h6>
        ${aisStatusHtml}
        <p>IMO: ${ship.imo || 'N/A'}</p>
        <p>Position: ${ship.position.lat.toFixed(5)}, ${ship.position.lon.toFixed(5)}</p>
        <p>Speed: ${ship.speed || '0'} knots</p>
        ${ship.destination ? `<p>Destination: ${ship.destination}</p>` : ''}
        ${ship.eta ? `<p>ETA: ${new Date(ship.eta).toLocaleString()}</p>` : ''}
      </div>
    `;
    }

    // Отцентрировать карту на выбранном корабле и показать попап
    setSelectedShip(deviceId) {
        this.selectedShipId = deviceId;

        if (this.shipMarkersGroup && deviceId) {
            const layers = this.shipMarkersGroup.getLayers();
            for (const layer of layers) {
                if (layer.shipDeviceId === deviceId) {
                    this.map.setView(layer.getLatLng(), Math.max(this.map.getZoom(), 8));
                    layer.openPopup();
                    break;
                }
            }
        }
    }

    // Удалить все маркеры
    destroy() {
        // Очищаем маршрут
        this.clearRoute();
        
        if (this.shipMarkersGroup) {
            if (this.map && this.map.hasLayer && this.map.hasLayer(this.shipMarkersGroup)) {
                this.map.removeLayer(this.shipMarkersGroup);
            }
            this.shipMarkersGroup.clearLayers && this.shipMarkersGroup.clearLayers();
            this.shipMarkersGroup = null;
        }

        this.selectedShipId = null;
        this.portsData = null;
    }
}